import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  TrendingUp, Sparkles, RefreshCw, Mail, Trash2, Loader2, AlertTriangle,
  CheckCircle2, XCircle, Clock, ArrowUpRight, ArrowDownRight, Bitcoin,
  Building2, Globe2, ThumbsUp, ThumbsDown, Settings, Save, Wallet, Shield,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// AdminPreInvest — daily AI-driven investment predictions tool.
// Generates 20 picks/day (10 long-term + 10 short-term) across US / JSE /
// Crypto by feeding live market data + news to Gemini 3 Pro. Tracks
// win/loss against price targets and emails the admin a daily summary.
// ---------------------------------------------------------------------------
const marketIcon = (m) => {
  if (m === 'crypto') return <Bitcoin className="w-3 h-3" />;
  if (m === 'jse') return <Building2 className="w-3 h-3" />;
  return <Globe2 className="w-3 h-3" />;
};

const marketLabel = (m) => ({ us: 'US', jse: 'JSE', crypto: 'CRYPTO' }[m] || (m || '').toUpperCase());

const fmtPrice = (val, ccy) => {
  if (val === null || val === undefined || isNaN(val)) return '—';
  const prefix = ccy === 'ZAR' ? 'R ' : '$';
  const abs = Math.abs(val);
  if (abs < 1 && abs > 0) return `${prefix}${val.toFixed(4)}`;
  return `${prefix}${val.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const statusBadge = (s) => {
  const map = {
    pending: { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: <Clock className="w-3 h-3" /> },
    active: { cls: 'bg-teal-100 text-teal-800 border-teal-200', icon: <TrendingUp className="w-3 h-3" /> },
    closed: { cls: 'bg-slate-100 text-slate-700 border-slate-300', icon: <CheckCircle2 className="w-3 h-3" /> },
  };
  const v = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${v.cls}`}>
      {v.icon}{(s || 'pending').toUpperCase()}
    </span>
  );
};

const outcomeBadge = (o) => {
  if (o === 'win') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white"><ThumbsUp className="w-3 h-3" />WIN</span>;
  if (o === 'lose') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500 text-white"><ThumbsDown className="w-3 h-3" />LOSE</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-600">OPEN</span>;
};

const movePct = (cur, base) => {
  if (!cur || !base) return null;
  return ((cur - base) / base) * 100;
};

const PredictionRow = ({ row, onMark, onDelete }) => {
  const move = movePct(row.current_price, row.initial_price);
  const moveColor = move == null ? 'text-slate-400' : move >= 0 ? 'text-emerald-600' : 'text-rose-600';
  const MoveIcon = move == null ? null : move >= 0 ? ArrowUpRight : ArrowDownRight;
  const slThreshold = row.stop_loss_pct && row.buy_price_target
    ? row.buy_price_target * (1 - row.stop_loss_pct / 100) : null;
  const anchors = Array.isArray(row.news_anchors) ? row.news_anchors.filter(Boolean) : [];
  return (
    <>
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`preinvest-row-${row.id}`}>
      <td className="p-2.5 align-top">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide bg-teal-700 text-white">
            {marketIcon(row.market)}{marketLabel(row.market)}
          </span>
          <span className="font-mono text-sm font-bold text-slate-800">{row.symbol}</span>
        </div>
        <div className="text-[11px] text-slate-500 truncate max-w-[180px]" title={row.display_name}>{row.display_name}</div>
        {row.position_zar > 0 && (
          <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-teal-50 text-teal-800 border border-teal-200" title="Suggested position size">
            <Wallet className="w-2.5 h-2.5" />R {Math.round(row.position_zar).toLocaleString()}
            {row.units > 0 && <span className="text-teal-600">· {row.units.toLocaleString(undefined, { maximumFractionDigits: 4 })} units</span>}
          </div>
        )}
      </td>
      <td className="p-2.5 align-top text-xs">
        <div className="text-slate-700"><span className="text-slate-400">Buy:</span> {fmtPrice(row.buy_price_target, row.currency)}</div>
        <div className="text-slate-700"><span className="text-slate-400">Sell:</span> {fmtPrice(row.sell_price_target, row.currency)}</div>
        <div className="text-[10px] text-slate-400 mt-1">{row.buy_date} → {row.sell_date}</div>
      </td>
      <td className="p-2.5 align-top text-xs">
        {slThreshold && (
          <div className="inline-flex items-center gap-1 text-rose-700 font-semibold" title={`Auto-close at LOSE if price hits ${fmtPrice(slThreshold, row.currency)}`}>
            <Shield className="w-3 h-3" />SL {row.stop_loss_pct}%
          </div>
        )}
        {!!row.trailing_stop_pct && (
          <div className="text-emerald-700 mt-0.5 text-[11px]" title="Trailing stop: locks in gains by exiting when price retraces by this % from its peak">
            ↘ TS {row.trailing_stop_pct}%
          </div>
        )}
        {row.outcome_reason && (
          <div className="text-[9px] uppercase text-slate-400 mt-1 tracking-wide">
            via {row.outcome_reason.replace('_', ' ')}
          </div>
        )}
      </td>
      <td className="p-2.5 align-top text-right text-xs">
        <div className="font-bold text-sm text-slate-800">{fmtPrice(row.current_price, row.currency)}</div>
        {MoveIcon && (
          <div className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${moveColor}`}>
            <MoveIcon className="w-3 h-3" />
            {Math.abs(move).toFixed(2)}%
          </div>
        )}
      </td>
      <td className="p-2.5 align-top text-center">
        {statusBadge(row.status)}
        <div className="mt-1">{outcomeBadge(row.outcome)}</div>
      </td>
      <td className="p-2.5 align-top text-right whitespace-nowrap">
        {!row.outcome && (
          <>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700 hover:bg-emerald-50" onClick={() => onMark(row.id, 'win')} data-testid={`preinvest-mark-win-${row.id}`} title="Mark as win">
              <ThumbsUp className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-700 hover:bg-rose-50" onClick={() => onMark(row.id, 'lose')} data-testid={`preinvest-mark-lose-${row.id}`} title="Mark as lose">
              <ThumbsDown className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50" onClick={() => onDelete(row.id)} data-testid={`preinvest-delete-${row.id}`} title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </td>
    </tr>
    {/* Thesis row — AI's reasoning + the news headlines it anchored on. Always
        visible so it's the first thing the eye lands on after the price. */}
    {(row.reasoning || anchors.length > 0) && (
      <tr className="bg-slate-50/60" data-testid={`preinvest-reasoning-${row.id}`}>
        <td colSpan={6} className="px-3 pb-3 pt-0">
          <div className="flex items-start gap-2 text-[12px] text-slate-600 leading-relaxed">
            <Sparkles className="w-3.5 h-3.5 text-teal-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              {row.reasoning && <p className="italic">{row.reasoning}</p>}
              {anchors.length > 0 && (
                <div className="mt-1 text-[10px] text-slate-400">
                  <span className="font-semibold uppercase tracking-wide">Anchored on:</span>{' '}
                  {anchors.slice(0, 2).map((a, i) => (
                    <span key={i} className="ml-1">
                      "{a}"{i < Math.min(anchors.length, 2) - 1 ? ' ·' : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    )}
    </>
  );
};

const PicksTable = ({ rows, title, onMark, onDelete, testid }) => (
  <Card className="overflow-hidden" data-testid={testid}>
    <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
      <h3 className="font-bold text-slate-800">{title}</h3>
      <Badge variant="outline">{rows.length} picks</Badge>
    </div>
    <CardContent className="p-0">
      {rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm">No picks yet — click "Generate Now".</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <th className="p-2.5 text-left">Asset / Position</th>
              <th className="p-2.5 text-left">Target</th>
              <th className="p-2.5 text-left">Risk</th>
              <th className="p-2.5 text-right">Current</th>
              <th className="p-2.5 text-center">Status</th>
              <th className="p-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <PredictionRow key={r.id} row={r} onMark={onMark} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>
);

const StatPill = ({ label, value, color = 'slate' }) => {
  const colors = {
    slate: 'bg-slate-100 text-slate-800',
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
  };
  return (
    <div className={`flex-1 min-w-[110px] rounded-lg p-3 border ${colors[color]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
};

const AdminPreInvest = () => {
  const [stats, setStats] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [todayBrief, setTodayBrief] = useState('');
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null);
  const [configDraft, setConfigDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState('all'); // all | today | active

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  });

  const loadAll = useCallback(async () => {
    try {
      const [s, p, h, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/preinvest/stats`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/preinvest/predictions`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/preinvest/history-chart?days=30`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/admin/preinvest/config`, { headers: authHeaders() }),
      ]);
      setStats(s.data);
      setPredictions(p.data || []);
      setTodayBrief((s.data?.today_batch || {}).market_brief || '');
      setHistory(h.data || []);
      setConfig(c.data);
      if (!configDraft) setConfigDraft(c.data);
    } catch (err) {
      toast.error('Failed to load Pre-Invest data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredPreds = useMemo(() => {
    if (filter === 'today') {
      const today = new Date().toISOString().slice(0, 10);
      return predictions.filter(p => p.generation_batch === today);
    }
    if (filter === 'active') return predictions.filter(p => p.status === 'active');
    return predictions;
  }, [predictions, filter]);

  const longTerm = filteredPreds.filter(p => p.type === 'long_term');
  const shortTerm = filteredPreds.filter(p => p.type === 'short_term');

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/preinvest/generate`, {}, { headers: authHeaders() });
      toast.success(`Generated ${r.data.inserted} predictions`);
      setTodayBrief(r.data.market_brief || '');
      await loadAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Generation failed: ${msg}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/preinvest/refresh-prices`, {}, { headers: authHeaders() });
      toast.success(`Refreshed ${r.data.updated} prices · ${r.data.evaluated} newly evaluated`);
      await loadAll();
    } catch (err) {
      toast.error('Price refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setEmailResult(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/preinvest/send-summary`, {}, { headers: authHeaders() });
      setEmailResult(r.data);
      if (r.data?.ok) {
        toast.success(`Email sent · status ${r.data.status_code}`);
      } else {
        toast.error(`Email failed: ${r.data?.sendgrid_body || r.data?.error || 'unknown'}`);
      }
    } catch (err) {
      toast.error('Email request failed');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMark = async (id, outcome) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/preinvest/predictions/${id}`, { outcome }, { headers: authHeaders() });
      toast.success(`Marked as ${outcome}`);
      await loadAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/preinvest/predictions/${confirmDelete}`, { headers: authHeaders() });
      toast.success('Deleted');
      setConfirmDelete(null);
      await loadAll();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleSaveConfig = async () => {
    if (!configDraft) return;
    setSavingConfig(true);
    try {
      const r = await axios.put(`${BACKEND_URL}/api/admin/preinvest/config`, {
        portfolio_size_zar: Number(configDraft.portfolio_size_zar),
        long_term_pct: Number(configDraft.long_term_pct),
        short_term_pct: Number(configDraft.short_term_pct),
      }, { headers: authHeaders() });
      setConfig(r.data);
      setConfigDraft(r.data);
      toast.success('Portfolio settings saved · position sizes recompute on next generate');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-preinvest-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-teal-600" />
              Pre-Invest
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              AI-driven daily predictions across US, JSE and Crypto · summary mailed to <code className="bg-slate-100 px-1 rounded">sales@brainwave-group.com</code> at 07:00 SAST · <a href="/predictions" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline" data-testid="preinvest-public-link">public landing page ↗</a>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleGenerate} disabled={generating} className="bg-teal-600 hover:bg-teal-700" data-testid="preinvest-generate-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? 'Generating…' : 'Generate Now'}
            </Button>
            <Button onClick={handleRefresh} disabled={refreshing} variant="outline" data-testid="preinvest-refresh-btn">
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh Prices
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail} variant="outline" data-testid="preinvest-email-btn">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send Summary Now
            </Button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 text-xs" data-testid="preinvest-disclaimer">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span><b>AI-generated commentary — not financial advice.</b> Predictions are produced by Gemini 3 Pro from public market data and news headlines. Always verify before acting and never risk capital you can't afford to lose.</span>
        </div>

        {/* Email result strip */}
        {emailResult && !emailResult.ok && (
          <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 text-rose-900 rounded-lg p-3 text-xs" data-testid="preinvest-email-error">
            <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <b>SendGrid error:</b> {emailResult.sendgrid_body || emailResult.error}
              {emailResult.sendgrid_body && emailResult.sendgrid_body.includes('credits') && (
                <div className="mt-1">Your SendGrid free-tier monthly quota has been used. Upgrade your SendGrid plan or wait until reset.</div>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="flex gap-3 flex-wrap" data-testid="preinvest-stats">
            <StatPill label="Total picks" value={stats.total} color="slate" />
            <StatPill label="Active" value={stats.active} color="teal" />
            <StatPill label="Pending" value={stats.pending} color="amber" />
            <StatPill label="Wins" value={stats.wins} color="emerald" />
            <StatPill label="Losses" value={stats.losses} color="rose" />
            <StatPill label="Win-rate" value={`${stats.win_rate_pct}%`} color="teal" />
          </div>
        )}

        {/* History chart + portfolio config */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2" data-testid="preinvest-history-chart">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-teal-600" />
                <h3 className="font-semibold text-slate-800 text-sm">30-day outcome history</h3>
              </div>
              {history.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-sm text-slate-400">
                  No batches yet — outcomes will plot here once predictions close.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={history} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#64748b" fontSize={10} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 6 }}
                      formatter={(v, n) => [v, n]}
                      labelFormatter={(d) => `Batch ${d}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="wins" stackId="a" fill="#10b981" name="Wins" />
                    <Bar dataKey="losses" stackId="a" fill="#ef4444" name="Losses" />
                    <Bar dataKey="open" stackId="a" fill="#cbd5e1" name="Open" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card data-testid="preinvest-config">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Settings className="w-4 h-4 text-teal-600" />
                <h3 className="font-semibold text-slate-800 text-sm">Portfolio sizing</h3>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Portfolio size (ZAR)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1000"
                  value={configDraft?.portfolio_size_zar ?? ''}
                  onChange={(e) => setConfigDraft({ ...configDraft, portfolio_size_zar: e.target.value })}
                  data-testid="preinvest-config-portfolio"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-500">Long-term %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={configDraft?.long_term_pct ?? ''}
                    onChange={(e) => setConfigDraft({ ...configDraft, long_term_pct: e.target.value })}
                    data-testid="preinvest-config-long-pct"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Short-term %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={configDraft?.short_term_pct ?? ''}
                    onChange={(e) => setConfigDraft({ ...configDraft, short_term_pct: e.target.value })}
                    data-testid="preinvest-config-short-pct"
                  />
                </div>
              </div>
              <div className="text-[10px] text-slate-400 leading-snug">
                Each pick gets <b>portfolio × pct</b> in R. USD picks auto-convert via live FX. Applies to <i>new</i> generations only.
              </div>
              <Button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className="bg-teal-600 hover:bg-teal-700 w-full"
                size="sm"
                data-testid="preinvest-config-save"
              >
                {savingConfig ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
                Save settings
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Market brief */}
        {todayBrief && (
          <Card className="bg-gradient-to-br from-teal-50 to-white border-teal-200" data-testid="preinvest-brief">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-teal-800 text-sm">Today's market brief</span>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{todayBrief}</p>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2" data-testid="preinvest-filters">
          {[
            ['all', 'All'],
            ['today', "Today's picks"],
            ['active', 'Active only'],
          ].map(([v, label]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition ${filter === v
                ? 'bg-teal-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`preinvest-filter-${v}`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : predictions.length === 0 ? (
          <Card data-testid="preinvest-empty">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-700">No predictions yet</h3>
              <p className="text-sm text-slate-500 mt-1 mb-4">Click "Generate Now" to fetch today's 20 picks.</p>
              <Button onClick={handleGenerate} disabled={generating} className="bg-teal-600 hover:bg-teal-700">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <PicksTable
              title="Long-term (~90-150 days)"
              rows={longTerm}
              onMark={handleMark}
              onDelete={(id) => setConfirmDelete(id)}
              testid="preinvest-long-term-table"
            />
            <PicksTable
              title="Short-term (~3-10 days)"
              rows={shortTerm}
              onMark={handleMark}
              onDelete={(id) => setConfirmDelete(id)}
              testid="preinvest-short-term-table"
            />
          </div>
        )}

        <ConfirmDialog
          open={!!confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          title="Delete prediction?"
          description="This action is permanent. Win/Loss stats will recompute."
          confirmLabel="Delete"
          destructive
          onConfirm={handleDelete}
          testId="preinvest-confirm-delete"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminPreInvest;
